import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, inject, ViewChild } from '@angular/core';
import { TableModule } from 'primeng/table';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { TagModule } from 'primeng/tag';
import { DropdownModule } from 'primeng/dropdown';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { AdminPanelService } from '../../services/admin-panel.service';
import { UtilsService, MessageType } from 'src/app/common';
import { Subject, takeUntil } from 'rxjs';
import { SortEvent } from 'primeng/api';
import { Table } from 'primeng/table';

@Component({
  selector: 'gig-dbc-orders',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    InputTextModule,
    MultiSelectModule,
    TagModule,
    DropdownModule,
    ButtonModule,
    ProgressSpinnerModule
  ],
  templateUrl: './dbc-orders.component.html',
  styleUrls: ['./dbc-orders.component.scss']
})
export class DbcOrdersComponent implements OnInit, OnDestroy {
  private admin = inject(AdminPanelService);
  private utils = inject(UtilsService);
  @ViewChild('dt') table?: Table;
  // Server-side pagination state
  orders: any[] = [];
  /** Cache of all rows loaded so far (for hybrid paging) */
  private cachedOrders: any[] = [];
  loading = true;
  totalRecords = 0;
  pageSize = 10; // current page size (kept for API param)
  currentPage = 1; // 1-based
  private localPageSize = 10; // for template binding

  expandedRows: Record<string, boolean> = {};
  globalFilter = '';
  statusOptions: string[] = ['Pending', 'Processing', 'Shipped'];
  filters: { orderId: string; user: string; status: string } = { orderId: '', user: '', status: '' };

  // Subscription management
  private destroy$ = new Subject<void>();

  // Track timeouts for cleanup
  private timeouts: number[] = [];

  ngOnInit(): void {
    // Subscribe to paged orders stream with automatic unsubscribe
    this.admin.pagedOrders$
      .pipe(takeUntil(this.destroy$))
      .subscribe(list => {
        this.orders = list;
        this.loading = false;
      });

    this.admin.ordersTotalCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe(count => {
        this.totalRecords = count || 0;
      });

    this.loadPage(1, this.pageSize, true);
  }

  /** Load a server-side page */
  async loadPage(page: number, size: number, force = false, extra?: { orderId?: string; userId?: string; status?: string; userInput?: string }) {
    this.loading = true;
    const safePage = page > 0 ? page : 1;
    const safeSize = size > 0 ? size : this.pageSize;
    this.currentPage = safePage;
    this.pageSize = safeSize;
    this.localPageSize = safeSize;
    const { orderId, userId, status, userInput } = extra || {};
    const onlyStatus = !!status && !orderId && !userId && !userInput;
    try {
      if (onlyStatus) await this.admin.getPagedOrdersByStatus(status, { page: safePage, pageSize: safeSize, force });
      else await this.admin.getPagedOrders({ page: safePage, pageSize: safeSize, orderId, userId, status, force });
    } finally {
      // Loading state will be managed by the subscription callback
    }
    // After service emits, merge into cache (subscription already updated orders)
    // We assume subscription already fired by now (async). Use setTimeout microtask to read updated list.
    const timeoutId = window.setTimeout(() => {
      const current = this.orders || [];
      if (safePage === 1 || force) {
        this.cachedOrders = [...current];
      } else {
        // append unique by id preserving order pages loaded
        const existingIds = new Set(this.cachedOrders.map(o => o.id));
        current.forEach(o => { if (!existingIds.has(o.id)) this.cachedOrders.push(o); });
      }
      // Ensure totalRecords never shrinks if backend omits totalCount
      if (this.totalRecords < this.cachedOrders.length) this.totalRecords = this.cachedOrders.length;
    }, 0);
    this.timeouts.push(timeoutId);
  }

  /** Apply filters via server-side query */
  applyFilters() {
    const orderId = this.filters.orderId?.trim() || undefined;
    const status = this.filters.status?.trim() || undefined;
    const userInput = this.filters.user?.trim();
    let userId: string | undefined;
    if (userInput && /^\d+$/.test(userInput)) userId = userInput; // interpret numeric input as ID
    this.loadPage(1, this.pageSize, true, { orderId, userId, status, userInput });
  }

  resetFilters() {
    this.filters = { orderId: '', user: '', status: '' };
    this.applyFilters();
  }

  /** PrimeNG paginator event -> call API when page OR size changes */
  onPage(event: any) {
    const rows = (typeof event?.rows === 'number' && event.rows > 0) ? event.rows : this.pageSize;
    let pageIndex: number | undefined;
    if (typeof event?.page === 'number') pageIndex = event.page; else if (typeof event?.first === 'number' && rows > 0) pageIndex = Math.floor(event.first / rows);
    const requestedPage = (typeof pageIndex === 'number' && pageIndex >= 0) ? pageIndex + 1 : this.currentPage;
    const sizeChanged = rows !== this.pageSize;

    // If page size decreased, just adjust local settings and slice cached data; no API call.
    if (sizeChanged && rows < this.pageSize) {
      this.pageSize = rows;
      this.localPageSize = rows;
      this.currentPage = 1; // reset to first page to avoid index overflow
      this.totalRecords = Math.max(this.totalRecords, this.cachedOrders.length);
      this.orders = this.cachedOrders.slice(0, rows);
      return;
    }

    // If only navigating pages and data already cached for the requested page range, slice.
    const endIndexNeeded = requestedPage * rows;
    if (!sizeChanged && endIndexNeeded <= this.cachedOrders.length) {
      this.currentPage = requestedPage;
      const start = (requestedPage - 1) * rows;
      this.orders = this.cachedOrders.slice(start, start + rows);
      return;
    }

    // If increasing page size and we already have enough cached rows to fill first page, just slice.
    if (sizeChanged && rows > this.pageSize && this.cachedOrders.length >= rows) {
      this.pageSize = rows;
      this.localPageSize = rows;
      this.currentPage = 1;
      this.orders = this.cachedOrders.slice(0, rows);
      return;
    }

    // Otherwise we need fresh server call (either larger page size than cache or navigating beyond cache)
    if (sizeChanged) {
      // Fetch new first page with new size to keep consistent ordering
      this.loadPage(1, rows);
    } else if (requestedPage !== this.currentPage) {
      this.loadPage(requestedPage, this.pageSize);
    }
  }

  refreshOrders(force = false) {
    this.loadPage(this.currentPage, this.pageSize, force, {
      orderId: this.filters.orderId?.trim() || undefined,
      status: this.filters.status?.trim() || undefined,
      userId: /^\d+$/.test(this.filters.user?.trim() || '') ? this.filters.user.trim() : undefined,
      userInput: this.filters.user?.trim()
    });
  }

  toggleRowExpansion(order: any) {
    if (this.expandedRows[order.id]) delete this.expandedRows[order.id]; else this.expandedRows[order.id] = true;
    this.expandedRows = { ...this.expandedRows };
  }

  onSort(event: SortEvent) {
    const field = event.field;
    const direction = event.order || 1;
    if (!field) return;
    this.orders = [...this.orders].sort((a, b) => {
      const v1 = this.resolveField(a, field);
      const v2 = this.resolveField(b, field);
      if (v1 == null && v2 != null) return -1 * direction;
      if (v1 != null && v2 == null) return 1 * direction;
      if (v1 == null && v2 == null) return 0;
      if (typeof v1 === 'string' && typeof v2 === 'string') return v1.localeCompare(v2) * direction;
      return (v1 < v2 ? -1 : v1 > v2 ? 1 : 0) * direction;
    });
  }

  private resolveField(obj: any, path: string): any {
    if (!obj || !path) return undefined;
    if (!path.includes('.')) return obj[path];
    return path.split('.').reduce((acc, k) => acc ? acc[k] : undefined, obj);
  }

  applyGlobalFilter(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.globalFilter = value;
    if (!this.table) return;
    const trimmed = value?.trim();
    if (trimmed) {
      this.table.filterGlobal(trimmed, 'contains');
    } else {
      this.table.clear();
    }
  }

  statusSeverity(status: string): string {
    if (!status) return 'secondary';
    const s = status.toLowerCase();
    if (s.includes('pending')) return 'warning';
    if (s.includes('process')) return 'info';
    if (s.includes('complete')) return 'success';
    if (s.includes('cancel') || s.includes('fail')) return 'danger';
    if (s.includes('ship')) return 'success';
    return 'secondary';
  }

  onStatusChange(order: any, newStatus: string) {
    if (!order) return;
    const prev = order.status;
    order.updating = true;
    this.admin.updateOrderStatusById(order.id, newStatus, order)
      .then(res => {
        if (!res.success) {
          order.status = prev;
          this.utils.errorMessage(MessageType.TOAST, 'Update Failed', `Order ${order.id} status not updated.`);
        } else {
          this.utils.successMessage(MessageType.TOAST, 'Status Updated', `Order ${order.id} set to ${newStatus}.`);
        }
      })
      .catch((error) => {
        order.status = prev;
        // this.utils.errorMessage(MessageType.TOAST, 'Update Error', `Order ${order.id} status reverted.`);
        this.utils.handleApiError(error, `Order ${order?.id} status reverted.`);
      })
      .finally(() => { order.updating = false; });
  }

  isImage(url: string | null | undefined): boolean {
    if (!url) return false;
    return /(\.png|\.jpe?g|\.gif|\.webp|\.svg)$/i.test(url.split('?')[0]);
  }

  onImageError(ev: Event) {
    const el = ev.target as HTMLImageElement;
    if (el) el.style.display = 'none';
  }

  // --- QR Code Helpers ---
  /** Safely extract QR code URL from the order object (supports different nesting possibilities). */
  getOrderQr(order: any): string | null {
    if (!order) return null;
    // Possible shapes (object with fileUrl OR direct string)
    const pickUrl = (val: any): string | null => {
      if (!val) return null;
      if (typeof val === 'string') return val;
      if (val.fileUrl && typeof val.fileUrl === 'string') return val.fileUrl;
      return null;
    };
    // Direct properties on order
    const direct = pickUrl(order.qrCode) || pickUrl(order.qr);
    if (direct) return direct;
    // Nested inside potential card object
    const nested = pickUrl(order.card?.qrCode) || pickUrl(order.card?.qr);
    return nested;
  }

  /** Download QR code image (forces browser download). */
  downloadQr(order: any): void {
    const url = this.getOrderQr(order);
    if (!url) return;
    const a = document.createElement('a');
    try {
      a.href = url;
      // Try to infer extension
      const extMatch = url.split('?')[0].match(/\.(png|jpg|jpeg|webp|gif|svg)$/i);
      const ext = extMatch ? extMatch[1] : 'png';
      a.download = `dbc-qr-${order?.id || 'code'}.${ext}`;
      document.body.appendChild(a);
      a.click();
    } finally {
      // Ensure cleanup even if an error occurs
      if (a.parentNode) {
        document.body.removeChild(a);
      }
    }
  }

  /** Open QR code in new browser tab (if present). */
  openQrInNewTab(order: any): void {
    const url = this.getOrderQr(order);
    if (!url) return;
    window.open(url, '_blank');
  }

  ngOnDestroy(): void {
    // Emit destroy signal to complete all subscriptions
    this.destroy$.next();
    this.destroy$.complete();

    // Clear all pending timeouts
    this.timeouts.forEach(id => clearTimeout(id));
    this.timeouts = [];

    // Clear table reference to prevent memory leaks
    if (this.table) {
      this.table.clear();
      this.table = undefined;
    }

    // Clear cached data
    this.cachedOrders = [];
    this.orders = [];
    this.expandedRows = {};
  }
}
